import type React from 'react';

import { CheckIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { SHARE_GOAL_COUNTER_STEPS } from '#src/app/pages/psf/share/shared/consts';

import { DRAW_DELAY_MS, DRAW_MS, POP_MS } from '../../constants';

/**
 * Keyframes from the prototype's animation spec. They stay local to this experiment arm (like the
 * new-recs-screen experiment's `CardRecentSigners`) so removing the experiment removes them too; keyframe
 * names are global to the page, hence the `share-counter-` prefix.
 */
const KEYFRAMES = `
@keyframes share-counter-pop {
	0% { transform: scale(0.3); opacity: 0; }
	55% { transform: scale(1.18); opacity: 1; }
	100% { transform: scale(1); }
}
@keyframes share-counter-draw {
	from { transform: scaleX(0); }
	to { transform: scaleX(1); }
}
@keyframes share-counter-burst-0 {
	0% { transform: translate(-50%, -50%) rotate(0); opacity: 1; }
	100% { transform: translate(-190%, -260%) rotate(220deg); opacity: 0; }
}
@keyframes share-counter-burst-1 {
	0% { transform: translate(-50%, -50%) rotate(0); opacity: 1; }
	100% { transform: translate(120%, -300%) rotate(-200deg); opacity: 0; }
}
@keyframes share-counter-burst-2 {
	0% { transform: translate(-50%, -50%) rotate(0); opacity: 1; }
	100% { transform: translate(-260%, 80%) rotate(300deg); opacity: 0; }
}
@keyframes share-counter-burst-3 {
	0% { transform: translate(-50%, -50%) rotate(0); opacity: 1; }
	100% { transform: translate(220%, 120%) rotate(-260deg); opacity: 0; }
}
`;

// The DS ProgressStepper can't be animated, so its nodes and connectors are copied over it. These mirror
// its large layout: 32px nodes joined by 24px connectors, centred, so node centres sit 56px apart.
const NODE_SIZE_PX = 32;
const CONNECTOR_WIDTH_PX = 24;
const NODE_SPACING_PX = NODE_SIZE_PX + CONNECTOR_WIDTH_PX;

const CONFETTI_COLORS = [
	'bg-fill-information-strong',
	'bg-fill-interactive',
	'bg-icon-information',
	'bg-fill-brand-strong',
] as const;

function animation(
	name: string,
	{ duration, delay = 0, easing = 'ease-out' }: Readonly<{ duration: number; delay?: number; easing?: string }>,
): React.CSSProperties {
	return {
		animationName: name,
		animationDuration: `${duration}s`,
		animationTimingFunction: easing,
		animationDelay: `${delay}s`,
		animationFillMode: 'both',
	};
}

const CONFETTI = Array.from({ length: 16 }, (_, i) => ({
	className: CONFETTI_COLORS[i % 4],
	style: animation(`share-counter-burst-${i % 4}`, {
		duration: Number((0.7 + (i % 5) * 0.12).toFixed(2)),
		delay: Number((0.15 + (i % 3) * 0.05).toFixed(2)),
		easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)',
	}),
}));

const POP_STYLE = animation('share-counter-pop', { duration: POP_MS / 1000 });
const DRAW_STYLE = animation('share-counter-draw', { duration: DRAW_MS / 1000, delay: DRAW_DELAY_MS / 1000 });

function nodeCenterOffset(stepIndex: number): number {
	return (stepIndex - (SHARE_GOAL_COUNTER_STEPS - 1) / 2) * NODE_SPACING_PX;
}

type Props = Readonly<{
	count: number;
	/**
	 * The DS stepper hasn't caught up with `count` yet, so the new step's pop (and connector) play over it.
	 */
	isRevealing: boolean;
}>;

/**
 * Decorative motion for the Share Goal Counter, drawn over the DS ProgressStepper: the new step pops in and
 * the next connector draws, and confetti bursts from the last step at 3/3.
 */
export const ShareGoalCounterEffects: React.FC<Props> = ({ count, isRevealing }) => {
	const isComplete = count === SHARE_GOAL_COUNTER_STEPS;
	if (!isRevealing && !isComplete) return null;

	const newStepIndex = count - 1;

	return (
		<>
			<style>{KEYFRAMES}</style>
			{isRevealing && (
				<div
					aria-hidden
					className="pointer-events-none absolute inset-0 motion-reduce:hidden"
					data-qa="share-goal-counter-reveal"
				>
					<span
						// Keyed by step so a new count overlapping the last pop still gets its own.
						key={`pop-${newStepIndex}`}
						className="bg-icon-information text-icon-inverse-strong border-stroke-info-strong absolute top-1/2 flex items-center justify-center rounded-full border-2 border-solid"
						style={{
							width: NODE_SIZE_PX,
							height: NODE_SIZE_PX,
							marginTop: -NODE_SIZE_PX / 2,
							left: `calc(50% + ${nodeCenterOffset(newStepIndex) - NODE_SIZE_PX / 2}px)`,
							...POP_STYLE,
						}}
					>
						<CheckIcon size="medium" />
					</span>
					{/* The last step has no connector after it. */}
					{!isComplete && (
						<span
							key={`draw-${newStepIndex}`}
							className="absolute top-1/2 h-0.5 origin-left bg-(--color-stroke-info-strong)"
							style={{
								width: CONNECTOR_WIDTH_PX,
								marginTop: -1,
								left: `calc(50% + ${nodeCenterOffset(newStepIndex) + NODE_SIZE_PX / 2}px)`,
								...DRAW_STYLE,
							}}
						/>
					)}
				</div>
			)}
			{isComplete && (
				<div
					aria-hidden
					className="pointer-events-none absolute inset-0 z-[2] motion-reduce:hidden"
					data-qa="share-goal-counter-confetti"
				>
					{CONFETTI.map(({ className, style }, i) => (
						<span
							key={i}
							className={cn('absolute top-1/2 h-3 w-2 rounded-[2px]', className)}
							style={{ left: `calc(50% + ${nodeCenterOffset(SHARE_GOAL_COUNTER_STEPS - 1)}px)`, ...style }}
						/>
					))}
				</div>
			)}
		</>
	);
};
